import React from 'react';
import type {
  YutiRecord,
  YutiSeverityTier,
  YutiDepth,
  ActionClass,
  YutiAspect,
} from './yutiTypes';

interface YutiCardProps {
  record: YutiRecord;
}

const DEPTH_LABELS: Record<YutiDepth, (record: YutiRecord) => string> = {
  rashi: (r) => `share ${r.rashi}`,
  nakshatra_fused: (r) => {
    const nak = r.planets[0]?.nakshatra ?? '';
    return `fuse in ${r.rashi}'s ${nak} nakshatra`;
  },
  pada_fused: (r) => {
    const nak = r.planets[0]?.nakshatra ?? '';
    return `unite within ${nak}'s pāda`;
  },
  degree_fused: () => `meet at the exact same degree`,
};

const CONTEXT_META: Record<ActionClass, { emoji: string; label: string }> = {
  arambha: { emoji: '🌅', label: 'New beginnings' },
  anushthana: { emoji: '🔁', label: 'Ongoing work' },
  nivritti: { emoji: '🍂', label: 'Closure' },
};

const SEVERITY_BORDER: Record<YutiSeverityTier, string> = {
  most_auspicious: 'border-emerald-400 dark:border-emerald-600',
  auspicious: 'border-emerald-300 dark:border-emerald-700',
  neutral: 'border-border',
  cautionary: 'border-red-300 dark:border-red-700',
  most_cautionary: 'border-red-400 dark:border-red-600',
};

const SEVERITY_BADGE: Record<YutiSeverityTier, string> = {
  most_auspicious: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  auspicious: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  neutral: 'bg-muted text-muted-foreground',
  cautionary: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
  most_cautionary: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
};

const TIER_VERDICT_LABEL: Record<YutiSeverityTier, string> = {
  most_auspicious: 'excellent',
  auspicious: 'favourable',
  neutral: 'neutral',
  cautionary: 'caution',
  most_cautionary: 'defer',
};

const headerEmoji = (record: YutiRecord): string => {
  if (record.tier_status === 'voided') return '⚠️';
  if (record.severity === 'most_auspicious' || record.severity === 'auspicious') return '✨';
  if (record.severity === 'cautionary' || record.severity === 'most_cautionary') return '⚠️';
  return '⚡';
};

const formatDegree = (deg: number): string => {
  const whole = Math.floor(deg);
  const mins = Math.round((deg - whole) * 60);
  return `${whole}°${mins.toString().padStart(2, '0')}'`;
};

const dignityChip = (planet: YutiRecord['planets'][number]): string | null => {
  if (planet.dignity) return planet.dignity;
  if (planet.is_combust) return 'Combust';
  if (planet.is_at_gandanta) return 'Gandānta';
  if (planet.is_retrograde) return 'Retrograde';
  if (planet.is_at_sandhi) return 'Sandhi';
  return null;
};

const AspectChip: React.FC<{ aspect: YutiAspect }> = ({ aspect }) => (
  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground border border-border/60">
    {aspect.planet} · {aspect.aspect_type} · {aspect.classical_role}
  </span>
);

export const YutiCard: React.FC<YutiCardProps> = ({ record }) => {
  const isVoided = record.tier_status === 'voided';
  const borderClass = isVoided
    ? 'border-amber-400 dark:border-amber-600'
    : SEVERITY_BORDER[record.severity];

  const depthDescriptor = DEPTH_LABELS[record.yuti_depth]?.(record) ?? '';

  return (
    <div className={`rounded-lg border-2 ${borderClass} bg-card p-3 text-sm`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="font-semibold text-foreground">
            {headerEmoji(record)} {record.named_yoga || record.title}
            {isVoided && (
              <span className="ml-1.5 text-amber-700 dark:text-amber-400">— VOIDED</span>
            )}
          </div>
          {depthDescriptor && record.yuti_depth !== 'rashi' && (
            <div className="text-xs text-muted-foreground italic mt-0.5">
              {depthDescriptor}
            </div>
          )}
        </div>
        <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-medium ${SEVERITY_BADGE[record.severity]}`}>
          {record.severity_score.toFixed(1)}
        </span>
      </div>

      {isVoided && record.tier_0_blocker && (
        <div className="mt-2 px-2 py-1.5 rounded bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
          <span className="font-medium">Blocker:</span> {record.tier_0_blocker}
        </div>
      )}

      <div className="mt-2 space-y-0.5 text-xs">
        {record.planets.map((p) => {
          const chip = dignityChip(p);
          return (
            <div key={p.name} className="flex items-baseline gap-2">
              <span className="font-medium text-foreground min-w-[5.5rem]">
                {p.sanskrit} ({p.name})
              </span>
              <span className="text-muted-foreground">{p.nakshatra}</span>
              <span className="text-muted-foreground tabular-nums">{formatDegree(p.deg_in_rashi)}</span>
              {chip && (
                <span className="px-1 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">
                  {chip}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-2 flex flex-wrap gap-1">
        <span className="px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">
          {record.naisargika_maitri}
        </span>
        <span className="px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">
          {record.tightness === 'tight' ? 'Tight' : 'Wide'} {record.spread_degrees.toFixed(1)}°
        </span>
        <span className="px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">
          {record.rashi}: {record.tattva}/{record.guna}
        </span>
        {record.stellium && (
          <span className="px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">
            Stellium
          </span>
        )}
      </div>

      {record.aspects_received.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {record.aspects_received.map((a, i) => (
            <AspectChip key={i} aspect={a} />
          ))}
        </div>
      )}

      {record.yoga_source && (
        <div className="mt-1.5 text-[10px] text-muted-foreground italic">
          {record.yoga_source}
          {record.yoga_source_confidence && ` · ${record.yoga_source_confidence}`}
        </div>
      )}

      {record.effect && (
        <p className="mt-2 text-xs text-foreground leading-relaxed m-0">
          {record.effect}
        </p>
      )}

      <div className="mt-2.5 pt-2 border-t border-border/60">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
          Today's dharma
        </div>
        <div className="space-y-0.5">
          {(Object.keys(CONTEXT_META) as ActionClass[]).map((ctx) => {
            const meta = CONTEXT_META[ctx];
            const score = record.contextual_severity[ctx];
            if (!score) return null;
            return (
              <div key={ctx} className="flex items-baseline gap-2 text-xs">
                <span className="min-w-[8.5rem] text-muted-foreground">
                  {meta.emoji} {meta.label}:
                </span>
                <span className="text-foreground">
                  {TIER_VERDICT_LABEL[score.tier]} ({score.score.toFixed(1)})
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
